// ---- Enums ----
export type PlaybookStatusCategory = 'todo' | 'in_progress' | 'done';
export type PlaybookErrorState = 'stuck' | 'sla_breached' | 'action_failed' | null;
export type TransitionTriggerMode = 'manual' | 'event' | 'deadline' | 'auto';
export type TransitionTriggeredBy = 'user' | 'event' | 'deadline' | 'auto' | 'admin_override';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'skipped';
export type ApprovalDecision = 'approved' | 'rejected';
export type TransitionButtonStyle = 'default' | 'primary' | 'success' | 'warning' | 'danger';

// ---- Core Shapes ----
export interface PlaybookStatus {
  id: string;
  name: string;
  slug: string;
  category: PlaybookStatusCategory;
  is_initial?: boolean;
  is_terminal?: boolean;
  sort_order?: number;
  sla_hours?: number | null;
  color?: string;
}

export interface PlaybookSummary {
  id: string;
  name: string;
  slug: string;
  description?: string;
  version: number;
  practice_areas: string[];
  jurisdiction_codes: string[];
  statuses?: PlaybookStatus[];
}

export interface PlaybookInstance {
  id: string;
  playbook_id: string;
  playbook_version: number;
  task_id: string;
  matter_id: string;
  realm_id: string;
  current_status_id: string;
  started_at: string;
  completed_at?: string | null;
  trigger_event_id?: string | null;
  trigger_document_id?: string | null;
  runtime_data: Record<string, unknown>;
  error_state?: PlaybookErrorState;
  stuck_reason?: string | null;
  playbook?: PlaybookSummary;
  currentStatus?: PlaybookStatus;
}

// ---- Transition Shapes ----
export interface TransitionForm {
  id: string;
  title?: string;
  formDefinition: Record<string, unknown>;
}

export interface AvailableTransition {
  transitionId: string;
  name: string;
  slug: string;
  toStatusId: string;
  toStatusName: string;
  toStatusSlug?: string;
  buttonLabel?: string;
  buttonStyle: string;
  confirmMessage?: string;
  isAllowed: boolean;
  blockedReason?: string;
  forms?: TransitionForm[];
  isCollaborative?: boolean;
  approvalChain?: ApprovalChainStep[];
}

export interface AvailableTransitionsResponse {
  currentStatus: PlaybookStatus;
  transitions: AvailableTransition[];
}

// ---- Execute Transition ----
export interface ExecuteTransitionRequest {
  formData?: Record<string, unknown>;
}

export interface ExecuteTransitionResponse {
  success: boolean;
  instanceId?: string;
  previousStatus?: { id: string; name: string; slug: string };
  newStatus?: { id: string; name: string; slug: string };
  actionsExecuted?: Array<{ type: string; success: boolean; error?: string }>;
  result?: 'pending_approval' | 'rejected';
  pendingApproval?: { transitionId: string; currentStep: number; totalSteps: number };
  rejectedBy?: string;
  comment?: string | null;
  blockedBy?: 'guard' | 'validator';
  guard?: { type: string; errorMessage: string };
  validationErrors?: Array<{ type: string; errorMessage: string }>;
}

// ---- Dry Run ----
export interface DryRunResponse {
  dryRun: true;
  success: boolean;
  wouldTransitionTo: { name: string; slug: string; category: string };
  guardsEvaluated: Array<{ guard_type: string; passed: boolean; reason: string }>;
  validatorsEvaluated: Array<{ validator_type: string; passed: boolean; details: string }>;
  actionsSimulated: Array<{ action_type: string; execution_order: number; preview: Record<string, unknown> }>;
  wouldSucceed: boolean;
  blockingGuards: string[];
  blockingValidators: string[];
}

// ---- Force Transition ----
export interface ForceTransitionRequest {
  bypassGuards?: boolean;
  bypassValidators?: boolean;
  reason: string;
}

// ---- Diagnostics ----
export interface InstanceDiagnostics {
  instance_id: string;
  playbook_name: string;
  current_status: { name: string; slug: string; category: string };
  time_in_current_status_hours: number;
  available_transitions: Array<{
    transition_id: string;
    name: string;
    slug: string;
    trigger_mode: string;
    guards: Array<{ guard_type: string; passed: boolean; reason: string }>;
    all_guards_pass: boolean;
  }>;
  stuck: boolean;
  stuck_reason: string | null;
  suggested_fixes: string[];
}

// ---- Transition Log ----
export interface TransitionLogEntry {
  id: string;
  instance_id: string;
  transition_id?: string | null;
  from_status_id?: string;
  to_status_id: string;
  triggered_by: TransitionTriggeredBy;
  triggered_by_user_id?: string;
  is_forced: boolean;
  forced_by_user_id?: string | null;
  override_reason?: string | null;
  triggered_by_event_id?: string | null;
  form_data?: Record<string, unknown> | null;
  actions_executed?: Record<string, unknown> | null;
  guards_evaluated?: Record<string, unknown> | null;
  validators_evaluated?: Record<string, unknown> | null;
  executed_at: string;
  transition?: { name: string; slug: string };
  fromStatus?: PlaybookStatus;
  toStatus: PlaybookStatus;
  triggeredByUser?: { user_id: string; first_name?: string; last_name?: string; email?: string };
  forcedByUser?: { user_id: string; first_name?: string; last_name?: string; email?: string };
}

// ---- Approvals ----
export interface ApprovalChainStep {
  step_order: number;
  required_role: string;
  is_optional: boolean;
  sla_hours?: number;
}

export interface PlaybookApproval {
  id: string;
  realm_id: string;
  instance_id: string;
  transition_id: string;
  step_order: number;
  required_role: string;
  is_optional: boolean;
  assigned_user_id?: string | null;
  status: ApprovalStatus;
  decision_by_user_id?: string | null;
  decision_comment?: string | null;
  decided_at?: string | null;
  sla_hours?: number | null;
  escalation_notified: boolean;
  created_at: string;
  updated_at: string;
  assignedUser?: { user_id: string; first_name?: string; last_name?: string };
  decisionByUser?: { user_id: string; first_name?: string; last_name?: string };
}

export interface ApproveTransitionRequest {
  decision: ApprovalDecision;
  comment?: string;
}

// ---- Phase IV: Library & Admin Types ----

export type PlaybookLifecycle = 'draft' | 'active' | 'superseded' | 'inactive' | 'archived';
export type PlaybookSource = 'manual' | 'ai_generated' | 'court_rules' | 'imported';

export interface PlaybookEntity {
  id: string;
  realm_id: string;
  name: string;
  slug: string;
  description?: string;
  version: number;
  source_playbook_id?: string | null;
  source_version?: number | null;
  lifecycle: PlaybookLifecycle;
  is_system: boolean;
  source: PlaybookSource;
  practice_areas: string[];
  jurisdiction_codes: string[];
  case_subtypes: string[];
  ai_generation_metadata?: Record<string, unknown>;
  diagram_layout?: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
  statuses?: PlaybookStatusFull[];
  transitions?: PlaybookTransitionFull[];
  status_count?: number;
  transition_count?: number;
}

export interface PlaybookStatusFull extends PlaybookStatus {
  playbook_id: string;
  description?: string;
  escalation_role?: string | null;
  position_x?: number;
  position_y?: number;
}

export interface PlaybookTransitionGuard {
  id: string;
  guard_type: string;
  configuration: Record<string, unknown>;
  error_message?: string;
  sort_order: number;
}

export interface PlaybookTransitionValidator {
  id: string;
  validator_type: string;
  configuration: Record<string, unknown>;
  error_message?: string;
  sort_order: number;
}

export interface PlaybookTransitionAction {
  id: string;
  action_type: string;
  configuration: Record<string, unknown>;
  execution_order: number;
  is_async: boolean;
  error_handling: 'log' | 'block' | 'rollback';
}

export interface PlaybookTransitionFull {
  id: string;
  playbook_id: string;
  name: string;
  slug: string;
  from_status_id?: string | null;
  to_status_id: string;
  is_global: boolean;
  is_looped: boolean;
  trigger_mode: TransitionTriggerMode;
  event_trigger?: Record<string, unknown>;
  deadline_trigger?: Record<string, unknown>;
  button_label?: string;
  button_style: string;
  confirm_message?: string;
  sort_order: number;
  is_collaborative: boolean;
  approval_chain?: Array<Record<string, unknown>>;
  on_rejection: 'block' | 'revert_to_previous' | 'revert_to_status';
  rejection_target_status_id?: string | null;
  guards?: PlaybookTransitionGuard[];
  validators?: PlaybookTransitionValidator[];
  actions?: PlaybookTransitionAction[];
  forms?: TransitionForm[];
  fromStatus?: PlaybookStatus;
  toStatus?: PlaybookStatus;
}

// ---- Library Listing ----
export interface PlaybookListResponse {
  playbooks: PlaybookEntity[];
  total: number;
  page: number;
  limit: number;
}

export interface PlaybookListFilters {
  practice_area?: string;
  lifecycle?: PlaybookLifecycle;
  source?: PlaybookSource;
  page?: number;
  limit?: number;
}

// ---- Simulation ----
export interface SimulationPath {
  statuses: Array<{ name: string; slug: string; category: string }>;
  transitions: Array<{
    name: string;
    slug: string;
    guardsResult: Array<{ guard_type: string; passed: boolean; reason: string }>;
    validatorsResult: Array<{ validator_type: string; passed: boolean }>;
    actionPreviews: Array<{ action_type: string; preview: Record<string, unknown> }>;
  }>;
  reachesTerminal: boolean;
  deadlinesGenerated: Array<{ rule: string; calculatedDate: Date }>;
  tasksGenerated: Array<{ title: string; dueDate?: Date | null }>;
  formsAttached: Array<{ formNumber: string; formTitle: string }>;
}

export interface SimulationResult {
  paths: SimulationPath[];
  deadEndStatuses: Array<{ name: string; slug: string }>;
  orphanTransitions: Array<{ name: string; slug: string; reason: string }>;
  warnings: string[];
  reachableStatuses: number;
  totalStatuses: number;
}

// ---- Validation ----
export interface ValidationError {
  type: 'dead_end' | 'orphan_transition' | 'unregistered_handler' | 'no_initial_status' | 'unreachable_status';
  severity: 'error' | 'warning';
  message: string;
  context?: Record<string, unknown>;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

// ---- Version History ----
export interface VersionHistoryEntry {
  id: string;
  version: number;
  lifecycle: PlaybookLifecycle;
  published_at?: string | null;
  created_at: string;
  statusCount: number;
  transitionCount: number;
  instanceCount: number;
}

// ---- Version Diff ----
export interface StatusDiff {
  slug: string;
  changeType: 'added' | 'removed' | 'modified';
  details?: string;
  old?: Record<string, unknown>;
  new?: Record<string, unknown>;
}

export interface TransitionDiff {
  slug: string;
  name: string;
  changeType: 'added' | 'removed' | 'modified';
  guardChanges?: Array<{ changeType: 'added' | 'removed' | 'modified'; guardType: string; detail?: string }>;
  validatorChanges?: Array<{ changeType: 'added' | 'removed' | 'modified'; validatorType: string; detail?: string }>;
  actionChanges?: Array<{ changeType: 'added' | 'removed' | 'modified'; actionType: string; detail?: string }>;
  configChanges?: Record<string, { old: unknown; new: unknown }>;
}

export interface PlaybookDiff {
  sourceId: string;
  targetId: string;
  sourceVersion: number;
  targetVersion: number;
  statusChanges: StatusDiff[];
  transitionChanges: TransitionDiff[];
  summary: {
    statusesAdded: number;
    statusesRemoved: number;
    statusesModified: number;
    transitionsAdded: number;
    transitionsRemoved: number;
    transitionsModified: number;
    guardsChanged: number;
  };
}

// ---- Upstream Changes ----
export interface UpstreamChanges {
  hasUpstreamChanges: boolean;
  sourceVersion?: number;
  latestSourceVersion?: number;
  diff?: PlaybookDiff;
}

// ---- Version Compatibility ----
export interface VersionCompatibility {
  compatible: boolean;
  autoMappedStatuses: Array<{ sourceSlug: string; targetSlug: string; sourceName: string; targetName: string }>;
  unmappedStatuses: Array<{ slug: string; name: string; instanceCount: number }>;
  riskLevel: 'low' | 'medium' | 'high';
  riskFactors: string[];
}

// ---- Migration ----
export interface MigrationResult {
  success: boolean;
  instanceId: string;
  previousPlaybookId: string;
  newPlaybookId: string;
  statusMapped: { from: string; to: string };
}

export interface BulkMigrationResult {
  success: boolean;
  totalMigrated: number;
  totalFailed: number;
  results: Array<{ instanceId: string; success: boolean; error?: string }>;
}

// ---- Schemes ----
export interface PlaybookScheme {
  id: string;
  realm_id: string;
  name: string;
  description?: string;
  practice_area?: string;
  jurisdiction_code?: string;
  is_default: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  mappings?: PlaybookSchemeMapping[];
}

export interface PlaybookSchemeMapping {
  id: string;
  scheme_id: string;
  task_type_id: string;
  playbook_id: string;
  taskType?: PlaybookTaskType;
  playbook?: PlaybookSummary;
}

export interface PlaybookTaskType {
  id: string;
  name: string;
  slug: string;
  category: 'standard' | 'subtask';
  description?: string;
  icon?: string;
  color?: string;
  practice_areas?: string[];
}

// ---- Analytics ----
export interface StatusDwellTime {
  statusSlug: string;
  statusName: string;
  avgHours: number;
  medianHours: number;
  p95Hours: number;
  instanceCount: number;
}

export interface GuardFailureRate {
  guardType: string;
  transitionSlug: string;
  failCount: number;
  totalEvaluations: number;
  failRate: number;
}

export interface ActionErrorRate {
  actionType: string;
  transitionSlug: string;
  errorCount: number;
  totalExecutions: number;
  errorRate: number;
}

export interface CompletionMetrics {
  totalInstances: number;
  completedCount: number;
  avgCompletionHours: number;
  medianCompletionHours: number;
  inProgressCount: number;
  stuckCount: number;
  cancelledCount: number;
}

export interface TriggerDistribution {
  user: number;
  event: number;
  deadline: number;
  auto: number;
  admin_override: number;
  total: number;
  percentages: Record<string, number>;
}

export interface SLAComplianceItem {
  statusSlug: string;
  statusName: string;
  slaHours: number;
  withinSLACount: number;
  breachedCount: number;
  complianceRate: number;
}

export interface AnalyticsOverview {
  completion: CompletionMetrics;
  triggers: TriggerDistribution;
  slaCompliance: SLAComplianceItem[];
}

export interface VersionComparisonItem {
  version: number;
  avgCompletionHours: number;
  completionRate: number;
  instanceCount: number;
}

export interface AnalyticsDateRange {
  from?: string;
  to?: string;
}

// ---- Webhook Deliveries ----
export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'retrying';

export interface WebhookDelivery {
  id: string;
  realm_id: string;
  instance_id: string;
  transition_id: string;
  action_id: string;
  delivery_id: string;
  url: string;
  method: string;
  request_headers: Record<string, unknown>;
  request_body?: unknown;
  response_status?: number;
  response_body?: string;
  error_message?: string;
  attempt_number: number;
  status: WebhookDeliveryStatus;
  created_at: string;
  delivered_at?: string | null;
}

export interface WebhookDeliveriesResponse {
  deliveries: WebhookDelivery[];
  total: number;
}

// ---- Phase V: Editor Types ----

export interface EditorNodePosition {
  x: number;
  y: number;
}

export interface CreateStatusRequest {
  name: string;
  slug: string;
  category: PlaybookStatusCategory;
  description?: string;
  is_initial?: boolean;
  is_terminal?: boolean;
  sla_hours?: number | null;
  escalation_role?: string | null;
  sort_order?: number;
  position_x?: number;
  position_y?: number;
}

export interface UpdateStatusRequest extends Partial<CreateStatusRequest> {}

export interface CreateTransitionRequest {
  name: string;
  slug: string;
  from_status_id?: string | null;
  to_status_id: string;
  trigger_mode: TransitionTriggerMode;
  event_trigger?: Record<string, unknown>;
  deadline_trigger?: Record<string, unknown>;
  button_label?: string;
  button_style?: string;
  confirm_message?: string;
  sort_order?: number;
  is_collaborative?: boolean;
  is_global?: boolean;
  is_looped?: boolean;
  approval_chain?: Array<Record<string, unknown>>;
  on_rejection?: 'block' | 'revert_to_previous' | 'revert_to_status';
  rejection_target_status_id?: string | null;
}

export interface UpdateTransitionRequest extends Partial<CreateTransitionRequest> {}

export interface CreateGuardRequest {
  guard_type: string;
  configuration: Record<string, unknown>;
  error_message?: string;
  sort_order?: number;
}

export interface UpdateGuardRequest extends Partial<CreateGuardRequest> {}

export interface CreateValidatorRequest {
  validator_type: string;
  configuration: Record<string, unknown>;
  error_message?: string;
  sort_order?: number;
}

export interface UpdateValidatorRequest extends Partial<CreateValidatorRequest> {}

export interface CreateActionRequest {
  action_type: string;
  configuration: Record<string, unknown>;
  execution_order?: number;
  is_async?: boolean;
  error_handling?: 'log' | 'block' | 'rollback';
}

export interface UpdateActionRequest extends Partial<CreateActionRequest> {}

export interface CreateFormRequest {
  title?: string;
  formDefinition: Record<string, unknown>;
}

export interface UpdateFormRequest extends Partial<CreateFormRequest> {}

export interface SaveDiagramLayoutRequest {
  diagram_layout: Record<string, unknown>;
}

export interface CourtRuleDeadline {
  id: string;
  statute_reference: string;
  description?: string;
  base_event: string;
  days: number;
  direction: 'before' | 'after';
  day_type: 'calendar' | 'business' | 'court';
  jurisdiction_code?: string;
}

// ---- Phase VII: AI Playbook Generation Types ----

export interface AIGeneratePlaybookParams {
  prompt: string;
  practiceArea?: string;
  jurisdictionCode?: string;
  complexity?: 'simple' | 'standard' | 'complex';
}

export interface AIGeneratedStatus {
  name: string;
  slug: string;
  category: 'todo' | 'in_progress' | 'done' | 'blocked';
  description: string;
  is_initial: boolean;
  is_terminal: boolean;
  sla_hours?: number;
  position_x: number;
  position_y: number;
}

export interface AIGeneratedTransition {
  name: string;
  slug: string;
  from_status_slug: string;
  to_status_slug: string;
  trigger_mode: 'manual' | 'event' | 'deadline' | 'auto';
  button_label?: string;
  guards: Array<{ guard_type: string; config: Record<string, unknown> }>;
  validators: Array<{ validator_type: string; config: Record<string, unknown> }>;
  actions: Array<{ action_type: string; config: Record<string, unknown>; execution_order: number }>;
}

export interface AIGeneratedPlaybook {
  name: string;
  slug: string;
  description: string;
  practiceArea: string;
  statuses: AIGeneratedStatus[];
  transitions: AIGeneratedTransition[];
  metadata: {
    generatedBy: 'ai';
    model: string;
    promptUsed: string;
    confidence: number;
  };
}

export interface MigrateTemplateParams {
  templateId: string;
  enhanceWithAI?: boolean;
}

export type GapType = 'missing_guard' | 'missing_validator' | 'dead_end_status' | 'unreachable_status' | 'missing_notification' | 'no_sla' | 'no_rejection_handler';
export type GapSeverity = 'high' | 'medium' | 'low';
export type GapSuggestedAction = 'add_guard' | 'add_validator' | 'add_action' | 'add_transition' | 'set_sla' | 'set_rejection_handler';

export interface GapItem {
  type: GapType;
  severity: GapSeverity;
  description: string;
  affectedEntity: { type: 'status' | 'transition'; slug: string; name: string };
  suggestedFix?: {
    action: GapSuggestedAction;
    config: Record<string, unknown>;
  };
}

export interface GapDetectionResult {
  gaps: GapItem[];
  overallScore: number;
  analysisMetadata: { model: string; analysisTimestamp: string };
}

export interface TransitionHelpResult {
  failedGuards: Array<{ guardType: string; reason: string; suggestedAction: string }>;
  failedValidators: Array<{ validatorType: string; details: string; suggestedAction: string }>;
  overallSuggestion: string;
}

export interface TaskTemplateSummary {
  id: string;
  name: string;
  description?: string;
  practice_area?: string;
  status?: string;
}

// ---- Lifecycle Colors ----
export const LIFECYCLE_COLORS: Record<
  PlaybookLifecycle,
  { bg: string; text: string; darkBg: string; darkText: string }
> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', darkBg: 'dark:bg-gray-700', darkText: 'dark:text-gray-300' },
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  superseded: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  inactive: { bg: 'bg-orange-100', text: 'text-orange-700', darkBg: 'dark:bg-orange-900/30', darkText: 'dark:text-orange-300' },
  archived: { bg: 'bg-rose-100', text: 'text-rose-700', darkBg: 'dark:bg-rose-900/30', darkText: 'dark:text-rose-300' },
};

export const SOURCE_COLORS: Record<
  PlaybookSource,
  { bg: string; text: string; darkBg: string; darkText: string }
> = {
  manual: { bg: 'bg-lex-accent-soft', text: 'text-lex-accent', darkBg: '', darkText: '' },
  ai_generated: { bg: 'bg-violet-100', text: 'text-violet-700', darkBg: 'dark:bg-violet-900/30', darkText: 'dark:text-violet-300' },
  court_rules: { bg: 'bg-teal-100', text: 'text-teal-700', darkBg: 'dark:bg-teal-900/30', darkText: 'dark:text-teal-300' },
  imported: { bg: 'bg-cyan-100', text: 'text-cyan-700', darkBg: 'dark:bg-cyan-900/30', darkText: 'dark:text-cyan-300' },
};

// ---- Color Token System ----
export const STATUS_CATEGORY_COLORS: Record<
  PlaybookStatusCategory | 'blocked',
  { bg: string; text: string; border: string; dot: string; darkBg: string; darkText: string; darkBorder: string }
> = {
  todo: {
    bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500',
    darkBg: 'dark:bg-gray-800', darkText: 'dark:text-gray-300', darkBorder: 'dark:border-gray-600',
  },
  in_progress: {
    bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500',
    darkBg: 'dark:bg-blue-900/30', darkText: 'dark:text-blue-300', darkBorder: 'dark:border-blue-700',
  },
  done: {
    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500',
    darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300', darkBorder: 'dark:border-emerald-700',
  },
  blocked: {
    bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500',
    darkBg: 'dark:bg-rose-900/30', darkText: 'dark:text-rose-300', darkBorder: 'dark:border-rose-700',
  },
};

// ---- Keyframes (injected via <style>) ----
export const PLAYBOOK_KEYFRAMES = `
@keyframes tooltipIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeSlideLeft {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes pulseOnce {
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes coverageDrillDown {
  from { opacity: 0; max-height: 0; }
  to   { opacity: 1; max-height: 600px; }
}
`;
